import { v4 as uuidv4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { createRenderAPI } from '../../../../../api/camera/renders/renderCamera';
import { RP_TYPE } from '../../../../../config/constant/unityConstants';
import { base64ToBlob, scaleResolution } from '../../../../../helpers/jsHelper';
import { errorToast, successToast } from '../../../../../helpers/toastHelper';
import {
  selectAllOnboardingData,
  selectCalculatedRoomId,
  selectIsLoading,
  selectProjectType,
  selectSelectedOnboardingMaterial,
  selectSelectedOnboardingOtherMaterial,
  selectSelectedOnboardingProduct,
  selectUnityData,
  setCuratorLoader,
} from '../../../../../redux/slicers/admin/curatorSlicer';
import {
  addPreview,
  addRender,
  selectActiveTab,
} from '../../../../../redux/slicers/admin/paginatedRenderPreviewsSlicer';
import { getAppState } from '../../../../../redux/store';
import { useUnityContext } from '../../../../container/unityContainer';
import { addIdsToRequestData } from '../../CuratorHeader/useSaveProject';
import { getRoomJsonFile } from '../../../../../pages/admin/curator/getRoomJsonFile';
import { updateCameraFrameWhileTakingScreenshot } from '../../../../container/SafeCameraFrame';
import { MAX_PREVIEW_RESOLUTION } from '../../../../../constants/constants';
import { isUserOnboarding, showHideTooltip } from '../../../../gamificationTooltip/helpers';
import { apiCreateGamifyRender } from '../../../../../helpers/api';
import { dispatcher } from '../../../../../helpers/projectHelper';
import { gamifyBoardingActions, STAGE } from '../../../../../redux/slicers/gamifySlicer';
import {
  RENDER_DETAILS_ROUTE_PATH,
  RENDER_DETAILS_ROUTE_PATH_V2,
} from '../../../../../constants/routePathConstants';
import { useRenderPreviewPopup } from '../../../../../hooks/useRenderPreviewPopup';
import {
  setCuratorNotSaved,
  setCuratorSaved,
} from '../../../../../pages/admin/curator/curatorState';
import { useUserContext } from '../../../../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { LogCurator } from '../../../../../helpers/logEvents';
import { Mixpanel, MxEvent } from '../../../../../common/Mixpanel';
import useCurrentOrganizationInfo from '../../../../../hooks/useCurrentOrganizationInfo';

export const useSaveRenderPreview = () => {
  const loading = useSelector(selectIsLoading);
  const dispatch = useDispatch();
  const { unityContext, handleCameraScreenshot } = useUnityContext();
  const { closePopup } = useRenderPreviewPopup();
  const isOnboardingActive = isUserOnboarding();
  const { currentUser, updateUserData } = useUserContext();
  const navigate = useNavigate();
  const { hideNewPlans, isFreemiumUser } = useCurrentOrganizationInfo();

  const parseCamera = (camera, dpi) => {
    return {
      id: uuidv4(), // we need to generate uuid so we have camera id in json
      cameraName: camera.cameraName,
      distanceToSet: camera.distanceToSet,
      fov: camera.fov,
      horizontalFov: camera.horizontalFov,
      xTransRot: camera.xTransRot,
      yTransRot: camera.yTransRot,
      zTransRot: camera.zTransRot,
      centerObjectxPos: camera.centerObjectxPos,
      centerObjectyPos: camera.centerObjectyPos,
      centerObjectzPos: camera.centerObjectzPos,
      isCameraCorrection: camera.isCameraCorrection,
      lensShiftY: camera.lensShiftY,
      cameraHeight: camera.cameraHeight,
      cameraRotX: camera.cameraRotX,
      cameraRotZ: camera.cameraRotZ,
      focalLength: camera.focalLength,
      isdepthOfField: camera.isdepthOfField,
      selectedObjectName: camera.selectedObjectName,
      blurIntensity: camera.blurIntensity,
      resoX: camera.resoX,
      resoY: camera.resoY,
      xPos: camera.xPos,
      zPos: camera.zPos,
      yPos: camera.yPos,
      // tags: camera.tags, // not needed
      isPredefinedCamera: camera.isPredefinedCamera,
      is_rendered: camera.is_rendered, // TODO: do we need it
      focusXPos: camera.focusXPos,
      focusYPos: camera.focusYPos,
      focusZPos: camera.focusZPos,
      canvas_width: camera.canvas_width || 0,
      canvas_height: camera.canvas_height || 0,
      // safeFrameWidth: camera.safeFrameWidth,
      // safeFrameHeight: camera.safeFrameHeight,
      sensor_width: camera.sensor_width || 0,
      sensor_height: camera.sensor_height || 0,
      fov_axis: camera.fov_axis,
      gate_fit: camera.gate_fit,
      ...(dpi ? { dpi } : {}),
    };
  };

  const saveRender = ({
    data,
    cameraList,
    onSuccess,
    originalTemplateId,
    lightRoomContentType,
    isCallback,
    mattressBuilderObj,
  }) => {
    runRenderPreview({
      data,
      type: RP_TYPE.RENDER,
      cameraList,
      onSuccess,
      originalTemplateId,
      lightRoomContentType,
      isCallback,
      mattressBuilderObj,
    });
  };

  const savePreview = ({
    data,
    cameraList,
    onSuccess,
    originalTemplateId,
    lightRoomContentType,
    isCallback,
    mattressBuilderObj,
  }) => {
    runRenderPreview({
      data,
      type: RP_TYPE.PREVIEW,
      cameraList,
      onSuccess,
      originalTemplateId,
      lightRoomContentType,
      isCallback,
      mattressBuilderObj,
    });
  };

  // call this function when you want to run render & preview
  const runRenderPreview = async ({
    data,
    type,
    cameraList,
    onSuccess,
    originalTemplateId,
    lightRoomContentType,
    isCallback,
    mattressBuilderObj,
  }) => {
    if (loading) {
      return;
    }

    if (!data.name || !data.name.trim()) {
      errorToast('Please enter a file name.');
      return;
    }

    if (!cameraList.length) {
      errorToast('Please select at least one camera');
      return;
    }

    dispatch(setCuratorLoader(false));
    const isPreview = type === RP_TYPE.PREVIEW;
    const isRender = type === RP_TYPE.RENDER;
    const isBuilder = Boolean(mattressBuilderObj);

    // selectedCameraRPList
    const cameras = cameraList.map((item) => parseCamera(item, data.dpi));

    if (isPreview) {
      cameras.map((val) => {
        const resoX = val.resoX;
        const resoY = val.resoY;
        const { x, y } = scaleResolution({
          x: resoX,
          y: resoY,
          max: MAX_PREVIEW_RESOLUTION,
        });
        val.resoX = parseInt(x);
        val.resoY = parseInt(y);
        return val;
      });
    }

    LogCurator('useSaveRenderPreview', cameras);

    if (isOnboardingActive) {
      const formData = new FormData();
      const roomJson = (await getRoomJsonFile())?.raw;

      if (!roomJson) {
        closePopup();
        setCuratorNotSaved();
        dispatch(setCuratorLoader(true));
        return;
      }

      roomJson.cameraInfo = [...cameras];
      roomJson.cameraList = [...cameras];
      let json_data = JSON.stringify(roomJson);
      const blob = new Blob([json_data], { type: 'application/json' });
      formData.append('json_file', blob, 'json_file.json');
      const allData = selectAllOnboardingData(getAppState());
      const product = selectSelectedOnboardingProduct(getAppState());
      const material = selectSelectedOnboardingMaterial(getAppState());
      const wall_material = selectSelectedOnboardingOtherMaterial(getAppState());
      formData.append('render_name', data.name);
      formData.append('room', allData?.room_id);
      formData.append('tutorialroom', allData?.tutorial_room_id);
      formData.append('product', product?.id);
      formData.append('material', material?.mainId);
      formData.append('other_object_material', wall_material?.mainId);
      apiCreateGamifyRender(formData)
        .then((res) => {
          LogCurator('apiCreateGamifyRender', res);
          if (!res) return;
          const id = res?.data?.render_id;
          closePopup();
          dispatcher(gamifyBoardingActions.setFinalProgress(true));
          setCuratorSaved();
          showHideTooltip(currentUser, updateUserData, {
            gamifyOnBoarding: {
              ...currentUser?.user?.other_info?.gamifyOnBoarding,
              data: {
                ...currentUser?.user?.other_info?.gamifyOnBoarding?.data,
                renderID: id,
              },
            },
          });
          setTimeout(() => {
            dispatcher(gamifyBoardingActions.setFinalProgressStage(STAGE.FINAL));
            navigate(RENDER_DETAILS_ROUTE_PATH_V2.replace(':renderId', `${id}?isOnboarding=true`), {
              replace: true,
            });
          }, 2000);
        })
        .catch((error) => {
          console.log('apiCreateGamifyRender error', error);
        })
        .finally(() => {
          dispatch(setCuratorLoader(true));
        });

      Mixpanel.track(true, MxEvent.GAMIFY_ONBOARDING_STEP_8, {
        'Image dpi': data?.dpi,
        'Image Type': data?.imageType,
        'Image stripe payment': data?.is_stripe_payment,
        'Image metadata': data?.metadata,
        'Image name': data?.name,
        'Image shadow on floor': data?.shadowOnFloor,
        'Image transparent bg': data?.transparentBg,
        'Image watermark': data?.watermark,
        'Image Camera List': cameraList,
      });

      Mixpanel.track(true, MxEvent.GAMIFY_ONBOARDING_COMPLETED);

      Mixpanel.people.set({
        'Onboarding compleate': 'yes',
      });
      return;
    }

    handleCameraScreenshot(cameras);

    const camScreenshot = [];

    unityContext.on('GetScreenShotForCamera', (camName, imageInfo) => {
      LogCurator(`camName: ${camName} &  imageInfo: ${imageInfo}`);

      const mimeType = data.imageType === 'jpg' ? 'jpeg' : data.imageType;
      const blob = base64ToBlob({ imageString: imageInfo, mimeType });

      const getCamResolution = cameras.find((a) => a.cameraName === camName);
      if (getCamResolution !== -1) {
        const { resoX: width, resoY: height } = getCamResolution;
        updateCameraFrameWhileTakingScreenshot({ width, height });
      }

      camScreenshot.push({
        camera_angle_name: camName,
        camera_angle_file: blob,
      });
    });

    unityContext.on('OnAllScreenShotProcessed', async () => {
      // remove 2 listeners we've added so they will not be triggered again by other camera render
      unityContext.removeEventListener('GetScreenShotForCamera');
      unityContext.removeEventListener('OnAllScreenShotProcessed');

      LogCurator('OnAllScreenShotProcessed', camScreenshot);

      const roomJson = (await getRoomJsonFile())?.raw;
      if (!roomJson) {
        dispatch(setCuratorLoader(true));
        return;
      }

      roomJson.cameraInfo = [...cameras];
      roomJson.cameraList = [...cameras];

      const unityData = selectUnityData(getAppState());

      if (unityData?.isRetailAutomationEditInCurator) {
        roomJson.isRetailAutomationEditInCurator = true;
      }

      LogCurator('sasha this is room json', roomJson);

      let camera_json_data = JSON.stringify(roomJson);

      const formObj = new FormData();
      const blob = new Blob([camera_json_data], { type: 'application/json' });
      formObj.append('is_preview', isPreview);
      formObj.append('is_main_render', isRender);
      formObj.append('render_name', data.name);
      formObj.append('count', cameras.length);
      formObj.append('json_file', blob, 'camera_info.json');

      formObj.append('is_meta_data', isBuilder ? false : data.metadata);
      formObj.append('is_water_mark', isBuilder ? false : data.watermark);
      formObj.append('is_transparent_bg', data.transparentBg);
      formObj.append('is_shadow_on_floor', data.shadowOnFloor);
      formObj.append('is_stripe_payment', isBuilder ? false : data.is_stripe_payment);
      formObj.append('render_extension', data.imageType);

      if (data?.is_free_credit_use) {
        formObj.append('is_free_credit_use', data.is_free_credit_use);
      }

      if (data.partial_stripe_payment) {
        formObj.append('partial_stripe_payment', data.partial_stripe_payment);
      }

      if (data.partial_credit_amount) {
        formObj.append('partial_credit_amount', data.partial_credit_amount);
      }

      if (isBuilder) {
        // add connections for mattress builder project
        const { roomId, projectId } = mattressBuilderObj;
        formObj.append('is_product_builder', true);
        formObj.append('type', '3');
        formObj.append('room', roomId);
        formObj.append('project', projectId);
      } else {
        // add connections to room and project
        const projectType = selectProjectType(getAppState());
        const calculatedRoomId = selectCalculatedRoomId(getAppState());
        formObj.append('type', lightRoomContentType || projectType);
        formObj.append('room', calculatedRoomId);
      }

      addIdsToRequestData({
        data: formObj,
        roomInfo: roomJson,
        includedIds: { products: true, textures: true },
      });

      camScreenshot?.map((a, i) => {
        formObj.append('camera_angle_name', a.camera_angle_name);
        formObj.append(
          'camera_angle_file',
          a.camera_angle_file,
          `${a.camera_angle_name}.${data.imageType}`
        );
        formObj.append('camera_angle_file_reso_x', cameras[i].resoX);
        formObj.append('camera_angle_file_reso_y', cameras[i].resoY);
      });

      LogCurator('camera formObj: ', formObj);
      LogCurator('sasha form data', data);

      const jobSendFrom = isBuilder
        ? 'Mattress Builder'
        : unityData?.isRetailAutomationEditInCurator
          ? 'Retail Automation'
          : 'Curator';

      const formDpi = data?.dpi;
      const formImageType = data?.imageType;
      const formIsStripePayment = isBuilder ? false : data?.is_stripe_payment;
      const formMetadata = isBuilder ? false : data?.metadata;
      const formName = data?.name;
      const formShadowOnFloor = data?.shadowOnFloor;
      const formTransparentBg = data?.transparentBg;
      const formWatermark = isBuilder ? false : data?.watermark;

      /* eslint-disable */
      createRenderAPI(formObj, {
        onSuccess: (data) => {
          if (!isOnboardingActive && isPreview) {
            Mixpanel.track(true, MxEvent.CURATOR_PREVIEW_IMAGE_JOB_SENT, {
              'Image dpi': formDpi,
              'Image Type': formImageType,
              'Image stripe payment': formIsStripePayment,
              'Image metadata': formMetadata,
              'Image name': formName,
              'Image shadow on floor': formShadowOnFloor,
              'Image transparent bg': formTransparentBg,
              'Image watermark': formWatermark,
              'Image Camera List': cameraList,
              'Job send from': jobSendFrom,
            });
          } else if (!isOnboardingActive && isRender) {
            Mixpanel.track(true, MxEvent.CURATOR_HIGH_RESOLUTION_IMAGE_JOB_SENT, {
              'Image dpi': formDpi,
              'Image Type': formImageType,
              'Image stripe payment': formIsStripePayment,
              'Image metadata': formMetadata,
              'Image name': formName,
              'Image shadow on floor': formShadowOnFloor,
              'Image transparent bg': formTransparentBg,
              'Image watermark': formWatermark,
              'Image Camera List': cameraList,
              'Job send from': jobSendFrom,
            });
          }

          const thumbnail = data?.images?.[0]?.thumbnail;
          if (thumbnail) {
            // download img to force cache
            const img = document.createElement('img');
            img.setAttribute('src', thumbnail);
            img.style.display = 'none';
            img.onload = () => {
              document.body.removeChild(img);
            };
            img.onerror = () => {
              document.body.removeChild(img);
            };

            document.body.appendChild(img);
          }

          // dispatch(paginatedRenderPreviewsActions.triggerRefetch());
          if (!isBuilder) {
            const addAction = isPreview ? addPreview : addRender;
            const activeTab = selectActiveTab(getAppState());
            dispatch(addAction({ data, activeTab }));
          }

          successToast(`Your image is being processed`);
          onSuccess?.();
        },
        onError: () => {
          dispatch(setCuratorLoader(true));
        },
        afterAll: () => {
          !isCallback && dispatch(setCuratorLoader(true));
        },
        hideNewPlans: hideNewPlans,
        isFreemiumUser: isFreemiumUser,
      });
    });
  };

  return {
    savePreview,
    saveRender,
    parseCamera,
  };
};
